import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  Slider,
  sliderCSSVars,
  tokens,
} from '@fluentui/react-components';
import { Speaker216Regular, SpeakerMute16Regular } from '@fluentui/react-icons';
import React, { useRef, useState } from 'react';

const { sliderDirectionVar, sliderProgressVar } = sliderCSSVars;

const styles = makeStyles({
  volumeControls: {
    rowGap: '0px',
    columnGap: '0px',
    display: 'flex',
    height: '24px',
    width: '148px',
  },
  volumeButton: {
    fontWeight: '400',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    height: '24px',
    minWidth: '24px',
    paddingTop: `${tokens.spacingVerticalXXS}`,
    paddingRight: `${tokens.spacingHorizontalXXS}`,
    paddingBottom: `${tokens.spacingVerticalXXS}`,
    paddingLeft: `${tokens.spacingHorizontalXXS}`,
    ...shorthands.border('none'),
    backgroundColor: `color-mix(in srgb, ${tokens.colorNeutralForeground1} 60%, transparent)`,
    color: tokens.colorNeutralBackground1,
    '&:hover': {
      backgroundColor: tokens.colorNeutralForeground1,
      color: tokens.colorNeutralBackground1,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '4px',
    },
    '[data-fui-focus-visible]': {
      backgroundColor: tokens.colorNeutralForeground1,
      color: tokens.colorNeutralBackground1,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '4px',
    },
  },
  volumeSlider: {
    fontWeight: '400',
    ...shorthands.border('1px'),
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '4px',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '0px',
    width: '124px',
    height: '24px',
    minHeight: '24px',
    minWidth: '124px',
    backgroundColor: `color-mix(in srgb, ${tokens.colorNeutralForeground1} 60%, transparent)`,
    '&:hover': {
      backgroundColor: tokens.colorNeutralForeground1,
    },
    '[data-fui-focus-within]': {
      backgroundColor: tokens.colorNeutralForeground1,
    },
  },
  volumeSliderThumb: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderColor(tokens.colorNeutralStroke1),
    ...shorthands.borderWidth('1px'),
  },
  volumeSliderRail: {
    backgroundImage: `linear-gradient(
          var(${sliderDirectionVar}),
          ${tokens.colorNeutralBackground1} 0%,
          ${tokens.colorNeutralBackground1} var(${sliderProgressVar}),
          ${tokens.colorNeutralStrokeAccessible} var(${sliderProgressVar})
        )`,
    '::before': {
      backgroundImage: 'none',
      '@media (forced-colors: active)': {
        backgroundImage: 'none',
      },
    },
  },
  volumeButtonFocused: {
    backgroundColor: tokens.colorNeutralForeground1,
    color: tokens.colorNeutralBackground1,
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '0px',
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '4px',
  },
});

const DELTA_DIVISOR = 20; // 20 gives 5% delta
const MAX_VOLUME = 1;

export const VolumeControls = ({ usePlayerControls, i18n }) => {
  const classes = styles();
  const { setVolume, volume } = usePlayerControls();

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const volumeSliderRef = useRef(null);
  const volumeButtonRef = useRef(null);

  const delta = MAX_VOLUME / DELTA_DIVISOR;

  const handleMuteClick = () => {
    const isMuted = volume === 0;
    const newVolume = isMuted ? 1 : 0;
    if (isMuted) {
      setVolume(newVolume);
    } else {
      setVolume(0);
    }
  };

  const handleVolumeButtonFocussed = () => {
    setShowVolumeSlider(true);
  };

  const handleOnBlur = (event) => {
    if (event && event.relatedTarget === volumeSliderRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleOnMouseEnter = () => {
    setShowVolumeSlider(true);
  };

  const handleOnMouseLeave = (event) => {
    if (event && event.relatedTarget === volumeSliderRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleSliderOnMouseLeave = (event) => {
    if (event && event.relatedTarget === volumeButtonRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleSliderOnBlur = (event) => {
    if (event && event.relatedTarget === volumeButtonRef.current) {
      setShowVolumeSlider(true);
    } else {
      setShowVolumeSlider(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      const newVolume = volume - delta;
      setVolume(Math.max(newVolume, 0));
    } else if (event.key === 'ArrowRight') {
      const newVolume = volume + delta;
      setVolume(Math.min(newVolume, MAX_VOLUME));
    }
  };

  return (
    <div className={mergeClasses(classes.volumeControls, 'clipchamp-player-volume-controls')}>
      <Button
        className={
                      showVolumeSlider
                          ? mergeClasses(classes.volumeButton, classes.volumeButtonFocused)
                          : mergeClasses('volume-btn', classes.volumeButton)
                  }
        icon={volume === 0 ? <SpeakerMute16Regular /> : <Speaker216Regular />}
        shape="rounded"
        onClick={handleMuteClick}
        onFocus={handleVolumeButtonFocussed}
        onBlur={handleOnBlur}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        ref={volumeButtonRef}
        aria-label={i18n && i18n.getString(_TL_('volume'))}
      />
      {showVolumeSlider && (
        <Slider
          ref={volumeSliderRef}
          className={classes.volumeSlider}
          thumb={{
                          className: classes.volumeSliderThumb,
                      }}
          rail={{
                          className: classes.volumeSliderRail,
                      }}
          value={volume}
          max={1}
          min={0}
          step={0.01}
          onMouseLeave={handleSliderOnMouseLeave}
          onBlur={handleSliderOnBlur}
          onChange={(_, sliderValue) => {
                          setVolume(sliderValue.value);
                      }}
          onKeyDown={handleKeyDown}
          size="small"
        />
          )}
    </div>
  );
};
